/**
 * Session store (zustand + persist).
 *
 * Holds the durable session, the flow position, and — critically — the
 * connection machine that models reconnection/resume from the start
 * (UX_SPEC §3.6, DATAROOM_AUDIT §4). Design intents encoded here:
 *
 *   • A network blip is NEVER a restart. `resumeToken` + `completedSteps` are
 *     persisted, so an accidental reload rehydrates mid-flow (fixes as-built R2
 *     where `beforeunload` marked the call CLOSED and forced a re-invite).
 *   • The link moves through an explicit machine (connected → reconnecting →
 *     resumed/suspended → …), never a silent CLOSED (fixes R3).
 *   • The verifier (agent) affinity is preserved across a resume.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  CheckKey,
  ConnectionState,
  PlanCheckDTO,
  QueuePosition,
  SessionChecksDTO,
  SessionState,
  StepId,
} from '@/types';
import { PENDING_QUEUE } from '@/types';
import { INITIAL_TENANT_ID, getTenant } from '@/theme/tenants';
import { isMockMode } from '@/api/client';
import type { ServedTenantBranding } from '@/theme/servedTenant';
import {
  canAdvance,
  nextStepId,
  FLOW_ORDER,
  STEPPER_STEPS,
  type CheckAdvanceState,
  type FlowGuards,
} from '@/flow/stepMachine';
import { buildFlowOrder, buildStepperSteps } from '@/flow/checkPlan';

interface DroomState {
  // --- session ---------------------------------------------------------------
  session: SessionState;
  // --- flow guards (UI affirmations) -----------------------------------------
  guards: FlowGuards;
  // --- plan-derived flow (KYCID-FSD-00013 §8) --------------------------------
  /** Write-once pinned flow key (null until the plan is fetched). */
  flowKey: string | null;
  /** The frozen per-session plan (empty ONLY before the read lands, or in mock mode). */
  plan: readonly PlanCheckDTO[];
  /**
   * 18-Aug RCA (owner item #2): whether the frozen plan is KNOWN. The old "empty plan ⇒ run the
   * hardcoded FLOW_ORDER" fail-safe was a silent client-side bypass — an unread plan defaulted
   * every check to OPTIONAL, so a REQUIRED DigiLocker rendered a skip and the be's queue gate then
   * refused the customer in a dead loop. Now: 'pending' until the read lands, 'ready' when a
   * non-empty plan is pinned, 'unknown' when the read failed or came back empty (outside mock).
   * The shell BLOCKS honestly on 'unknown' with a retry — never runs a guessed flow (non-negotiable
   * #5: never hardcode an onboarding flow).
   */
  planState: 'pending' | 'ready' | 'unknown';
  /** Derived full step order — defaults to the legacy FLOW_ORDER. */
  flowOrder: readonly StepId[];
  /** Derived stepper subset — defaults to the legacy STEPPER_STEPS. */
  stepperSteps: readonly StepId[];
  /** Per-check advance state keyed by CheckKey (set by useCheckStepFooter). */
  checkGuards: Readonly<Record<string, CheckAdvanceState>>;
  // --- served tenant branding (retest item 4) --------------------------------
  /**
   * The SANITISED branding block the be served on the session-entry/bootstrap
   * read (null = the server said nothing — older be, or no branding
   * configured). Served branding WINS over the local fallback registry;
   * resolution happens in `theme/servedTenant.resolveTenant`.
   */
  servedBranding: ServedTenantBranding | null;
  /**
   * VC-12: the session pack's customer languages (`packLocales` on the session read, be V223) — `[en, ar]` on a Gulf
   * pack, null on every India session. The i18n runtime offers a pack-gated language (Arabic) only when listed here.
   */
  packLocales: readonly string[] | null;
  // --- live connection -------------------------------------------------------
  connection: ConnectionState;
  /** ms epoch the current connection state began (for elapsed displays). */
  connectionSince: number;
  reconnectAttempts: number;
  // --- queue -----------------------------------------------------------------
  queue: QueuePosition;

  // --- session lifecycle -----------------------------------------------------
  initSession: (partial: Partial<SessionState>) => void;
  reset: () => void;

  // --- flow navigation -------------------------------------------------------
  goToStep: (step: StepId) => void;
  completeCurrentAndAdvance: () => void;
  setConsentAffirmed: (v: boolean) => void;
  setCaptureQualityOk: (v: boolean) => void;

  // --- plan-derived flow -----------------------------------------------------
  /**
   * Pin the frozen plan (from `GET /sessions/{id}/checks`) ONCE. An empty plan
   * keeps today's legacy flow (the documented fail-safe); a non-empty plan
   * derives the order + stepper via `flow/checkPlan.ts`.
   */
  initCheckPlan: (dto: Pick<SessionChecksDTO, 'plan'> & { flowKey?: string | null }) => void;
  /** Record a check's advance state (satisfied|fallback|skipped) — powers canAdvance. */
  setCheckGuard: (key: CheckKey, state: CheckAdvanceState) => void;
  /**
   * The plan read FAILED (401/404/5xx/network). Outside mock mode this marks the plan 'unknown'
   * unless a non-empty plan is already pinned (a reload carries the persisted frozen plan — the
   * plan is write-once server-side, so keeping it is honest). Mock mode keeps its demo flow.
   */
  markPlanUnknown: () => void;
  /** A retry is in flight — back to 'pending' so the shell shows the preparing state, not the error. */
  beginPlanRead: () => void;
  /** Pin the served branding block (sanitised). Never called with null — absence keeps what we have. */
  setServedBranding: (branding: ServedTenantBranding) => void;
  setPackLocales: (locales: readonly string[] | null) => void;

  // --- queue -----------------------------------------------------------------
  setQueue: (q: QueuePosition) => void;

  // --- connection machine (the resume model) ---------------------------------
  setConnecting: () => void;
  setConnected: () => void;
  /** Blip detected — begin an in-place recovery (calm overlay, lock held). */
  beginReconnect: () => void;
  /** ICE-restart / short-gap success: resume in place, nothing re-done. */
  resumeInPlace: () => void;
  /** Longer drop: session parks as SUSPENDED with a time-boxed resume link. */
  suspend: () => void;
  /** Customer taps the resume link: rejoin SAME session, SAME steps, SAME agent. */
  resumeFromToken: (resumeToken: string) => void;
  /** Resume window elapsed — terminal. */
  expire: () => void;
  /**
   * The officer finished and the server closed the room — a normal ending, not a fault.
   * Marks the session 'submitted' (customer done, gated on the concurrent audit), never
   * 'expired': a completed verification must not be reported to the customer as a lapse.
   */
  endByServer: () => void;
  /**
   * 18-Aug RCA #11 — the VERIFIER dropped and did not return within the grace window. Terminal
   * for THIS call, distinct from 'ended' (officer finished → completion) and 'expired' (resume
   * window lapsed): the session status is left exactly as it was — NOT 'submitted' (nothing was
   * submitted) and NOT 'expired' (the customer did nothing wrong). The customer sees an apology,
   * not a completion screen; the be's agent-drop sweeper owns the FSM terminal.
   */
  endForVerifierAbsence: () => void;
  /**
   * Customer taps "Rejoin now" after a SUSPENDED drop — re-enter the SAME room IN PLACE (§5.4).
   *
   * Bumping this nonce re-runs the live-call connect effect: the join token is re-minted and the room
   * reconnected without leaving the page. The previous implementation navigated to /call/:sessionId, which
   * re-mounted the whole app and therefore re-ran consent, geo and the camera-permission prompt on every
   * reconnect. On a 250 kbps link that IS the "can't rejoin" experience, even when rejoin technically works
   * — the customer is asked to re-grant everything at the exact moment their connection is worst.
   */
  rejoinNonce: number;
  rejoinNow: () => void;
}

const initialSession = (): SessionState => ({
  sessionId: null,
  resumeToken: null,
  tenantId: INITIAL_TENANT_ID,
  regime: getTenant(INITIAL_TENANT_ID).regime,
  reference: null,
  customerName: null,
  agentId: null,
  agentName: null,
  currentStep: 'welcome',
  completedSteps: [],
  status: 'open',
});

const now = () => Date.now();

export const useSessionStore = create<DroomState>()(
  persist(
    (set, get) => ({
      session: initialSession(),
      guards: { consentAffirmed: false, captureQualityOk: false },
      flowKey: null,
      plan: [],
      planState: 'pending',
      flowOrder: FLOW_ORDER,
      stepperSteps: STEPPER_STEPS,
      checkGuards: {},
      servedBranding: null,
      packLocales: null,
      connection: 'idle',
      connectionSince: now(),
      rejoinNonce: 0,
      reconnectAttempts: 0,
      queue: PENDING_QUEUE,

      initSession: (partial) =>
        set((s) => ({ session: { ...s.session, ...partial } })),

      reset: () =>
        set({
          session: initialSession(),
          guards: { consentAffirmed: false, captureQualityOk: false },
          flowKey: null,
          plan: [],
          planState: 'pending',
          flowOrder: FLOW_ORDER,
          stepperSteps: STEPPER_STEPS,
          checkGuards: {},
          servedBranding: null,
          packLocales: null,
          connection: 'idle',
          connectionSince: now(),
          reconnectAttempts: 0,
          queue: PENDING_QUEUE,
        }),

      initCheckPlan: (dto) => {
        const plan = dto.plan ?? [];
        if (plan.length === 0) {
          // MOCK mode (no backend) keeps its demo flow — the legacy FLOW_ORDER — and is 'ready'.
          // Against a REAL backend an empty plan is impossible (the regime floor guarantees rows),
          // so it means "we do not know what this customer owes": mark 'unknown' and let the
          // shell block honestly. The legacy order is deliberately NOT run (18-Aug RCA #2).
          set({
            flowKey: dto.flowKey ?? null,
            plan: [],
            planState: isMockMode() ? 'ready' : 'unknown',
            flowOrder: FLOW_ORDER,
            stepperSteps: STEPPER_STEPS,
          });
          return;
        }
        set({
          flowKey: dto.flowKey ?? null,
          plan,
          planState: 'ready',
          flowOrder: buildFlowOrder(plan),
          stepperSteps: buildStepperSteps(plan),
        });
      },

      markPlanUnknown: () =>
        set((s) => {
          if (isMockMode()) return { planState: 'ready' };
          if (s.plan.length > 0) return { planState: 'ready' };
          return { planState: 'unknown' };
        }),

      beginPlanRead: () =>
        set((s) => (s.plan.length > 0 ? { planState: 'ready' } : { planState: 'pending' })),

      setCheckGuard: (key, state) =>
        set((s) => ({ checkGuards: { ...s.checkGuards, [key]: state } })),

      setServedBranding: (branding) => set({ servedBranding: branding }),
      setPackLocales: (locales) => set({ packLocales: locales && locales.length > 0 ? [...locales] : null }),

      goToStep: (step) =>
        set((s) => ({ session: { ...s.session, currentStep: step } })),

      completeCurrentAndAdvance: () => {
        const { session, guards, plan, flowOrder, checkGuards } = get();
        const step = session.currentStep;
        // Thread the per-check advance state + the frozen plan into the guard.
        if (!canAdvance(step, { ...guards, checks: checkGuards }, plan)) return;
        const completed = session.completedSteps.includes(step)
          ? session.completedSteps
          : [...session.completedSteps, step];
        set({
          session: {
            ...session,
            completedSteps: completed,
            currentStep: nextStepId(step, flowOrder),
          },
        });
      },

      setConsentAffirmed: (v) =>
        set((s) => ({ guards: { ...s.guards, consentAffirmed: v } })),
      setCaptureQualityOk: (v) =>
        set((s) => ({ guards: { ...s.guards, captureQualityOk: v } })),

      setQueue: (q) => set({ queue: q }),

      // ---- connection machine --------------------------------------------------
      setConnecting: () =>
        set({ connection: 'connecting', connectionSince: now() }),

      setConnected: () =>
        set((s) => ({
          connection: 'connected',
          connectionSince: now(),
          reconnectAttempts: 0,
          session: { ...s.session, status: 'live' },
        })),

      beginReconnect: () =>
        set((s) => ({
          connection: 'reconnecting',
          connectionSince: now(),
          reconnectAttempts: s.reconnectAttempts + 1,
        })),

      resumeInPlace: () =>
        set((s) => ({
          connection: 'resumed',
          connectionSince: now(),
          session: { ...s.session, status: 'live' },
        })),

      suspend: () =>
        set((s) => ({
          connection: 'suspended',
          connectionSince: now(),
          // NOT closed — resumable. A resume token would be (re)issued server-side.
          session: {
            ...s.session,
            status: 'suspended',
            resumeToken: s.session.resumeToken ?? mockResumeToken(),
          },
        })),

      resumeFromToken: (resumeToken) =>
        set((s) => ({
          connection: 'resumed',
          connectionSince: now(),
          // Same session id, same completed steps, same agent affinity retained.
          session: { ...s.session, resumeToken, status: 'live' },
        })),

      expire: () =>
        set((s) => ({
          connection: 'expired',
          connectionSince: now(),
          session: { ...s.session, status: 'expired' },
        })),

      endByServer: () =>
        set((s) => ({
          connection: 'ended',
          connectionSince: now(),
          session: { ...s.session, status: 'submitted' },
        })),

      endForVerifierAbsence: () =>
        set({
          connection: 'verifier_lost',
          connectionSince: now(),
          // session.status deliberately untouched — see the interface note.
        }),

      // §5.4 — in-place rejoin. Moves to 'reconnecting' (never straight to connected: the transport
      // confirms that, not our optimism) and bumps the nonce the live-call effect depends on.
      rejoinNow: () =>
        set((s) => ({
          connection: 'reconnecting',
          connectionSince: now(),
          rejoinNonce: s.rejoinNonce + 1,
        })),
    }),
    {
      name: 'vcip-droom-session',
      storage: createJSONStorage(() => sessionStorage),
      // Persist ONLY the resume-critical slice. On reload this rehydrates the
      // flow mid-journey — the customer resumes, never restarts. The pinned
      // flowKey + plan ride along so the plan-derived order survives a reload
      // (re-fetched server-authoritatively on /resume regardless).
      partialize: (s) => ({
        session: {
          sessionId: s.session.sessionId,
          resumeToken: s.session.resumeToken,
          tenantId: s.session.tenantId,
          reference: s.session.reference,
          currentStep: s.session.currentStep,
          completedSteps: s.session.completedSteps,
        },
        flowKey: s.flowKey,
        plan: s.plan,
        // The served branding rides along so a reload does not flash the fallback
        // registry brand before the next server read re-pins it.
        servedBranding: s.servedBranding,
        // VC-12: so a reload mid-journey keeps the Arabic offer before the next session read re-pins it
        packLocales: s.packLocales,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<DroomState>;
        const plan = p.plan ?? current.plan;
        // Recompute the derived order from the persisted plan (empty = not yet read).
        const derived =
          plan.length > 0
            ? { flowOrder: buildFlowOrder(plan), stepperSteps: buildStepperSteps(plan) }
            : { flowOrder: FLOW_ORDER as readonly StepId[], stepperSteps: STEPPER_STEPS as readonly StepId[] };
        return {
          ...current,
          session: { ...current.session, ...(p.session ?? {}) },
          flowKey: p.flowKey ?? current.flowKey,
          plan,
          // A persisted non-empty plan is known; otherwise the route's bootstrap decides.
          planState: plan.length > 0 ? 'ready' : 'pending',
          servedBranding: p.servedBranding ?? current.servedBranding,
          packLocales: p.packLocales ?? current.packLocales,
          ...derived,
        };
      },
    },
  ),
);

/**
 * Client-side placeholder resume token. The REAL token is minted server-side
 * and time-boxed; this only lets the skeleton exercise the resume path.
 */
function mockResumeToken(): string {
  const rnd =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `rt_${rnd}`;
}
